import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { STUDENT_NAV } from "@/lib/studentNav";
import { DashboardShell } from "@/components/DashboardShell";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Trash2, Loader2, ArrowLeft, ShieldCheck, Minus, Plus, BadgePercent } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { clearPaperCart, getPaperCart, getPaperLinePricing, setPaperCart, updatePaperQuantity } from "@/lib/paperCart";

const NAV = STUDENT_NAV;

export default function Checkout() {
  const [papers, setPapers] = useState([]);
  const [cart, setCart] = useState(getPaperCart());
  const [mode, setMode] = useState("test");
  const [paying, setPaying] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/paper-store"), api.get("/payments/config")])
      .then(([store, config]) => {
        setPapers(store.data);
        setMode(config.data.mode);
        const byId = new Map(store.data.map((paper) => [paper.id, paper]));
        const next = setPaperCart(getPaperCart()
          .filter((item) => (byId.get(item.paper_id)?.available_sets || 0) > 0)
          .map((item) => ({ ...item, quantity: Math.min(item.quantity, byId.get(item.paper_id).available_sets) })));
        setCart(next);
      })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
  }, []);

  const items = useMemo(() => cart.map((item) => ({ ...papers.find((paper) => paper.id === item.paper_id), quantity: item.quantity })).filter((paper) => paper.id), [cart, papers]);
  const setQuantity = (paper, quantity) => setCart(updatePaperQuantity(paper.id, Math.min(quantity, paper.available_sets)));
  const totalQuantity = items.reduce((sum, paper) => sum + paper.quantity, 0);
  const linePricing = (paper) => getPaperLinePricing(paper.available_set_prices || [], paper.quantity);
  const subtotal = items.reduce((sum, paper) => sum + linePricing(paper).subtotal, 0);
  const discount = items.reduce((sum, paper) => sum + linePricing(paper).discount, 0);
  const total = subtotal - discount;
  const remove = (id) => setCart(setPaperCart(cart.filter((item) => item.paper_id !== id)));

  const complete = () => {
    clearPaperCart();
    toast.success(`${totalQuantity} paper set${totalQuantity === 1 ? "" : "s"} purchased successfully`);
    navigate("/student");
  };

  const pay = async () => {
    if (!cart.length) return;
    setPaying(true);
    try {
      const { data } = await api.post("/payments/create-paper-order", { items: cart });
      if (data.mode === "test") {
        await api.post("/payments/verify-paper-order", { order_id: data.order.id });
        complete();
        return;
      }
      if (!window.Razorpay) throw new Error("Payment checkout could not be loaded. Please refresh and try again.");
      const checkout = new window.Razorpay({
        key: data.key_id, amount: data.order.amount, currency: data.order.currency,
        name: "ThetaExams", description: `${totalQuantity} question paper set${totalQuantity === 1 ? "" : "s"}`,
        order_id: data.order.id,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#7c3aed" }, modal: { ondismiss: () => setPaying(false) },
        handler: async (result) => {
          try {
            await api.post("/payments/verify-paper-order", { order_id: result.razorpay_order_id, payment_id: result.razorpay_payment_id, signature: result.razorpay_signature });
            complete();
          } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
          } finally { setPaying(false); }
        },
      });
      checkout.on("payment.failed", (response) => {
        toast.error(response.error?.description || "Payment failed. Please try again.");
        setPaying(false);
      });
      checkout.open();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  return (
    <DashboardShell nav={NAV} title="Checkout">
      <div className="mx-auto mb-4 flex max-w-5xl items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-primary-foreground" data-testid="three-set-discount-banner">
        <BadgePercent className="h-4 w-4 shrink-0" aria-hidden="true" /> Purchase 3 sets to get 10% discount
      </div>
      <div className="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[1fr_340px]">
        <section className="rounded-lg border border-border bg-background p-4 sm:p-6">
          <div className="mb-5 flex items-center justify-between">
            <div><h2 className="font-heading text-xl font-bold">Your cart</h2><p className="text-sm text-muted-foreground">{totalQuantity} paper set{totalQuantity === 1 ? "" : "s"} selected</p></div>
            <Link to="/student/plans"><Button variant="outline" size="sm" className="gap-2 rounded-full"><ArrowLeft className="h-4 w-4" /> Add more</Button></Link>
          </div>
          <div className="space-y-3">
            {items.map((paper) => (
              <div key={paper.id} className="flex flex-col gap-4 rounded-md border border-border p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`checkout-paper-${paper.id}`}>
                <div className="min-w-0"><p className="font-medium">{paper.subject} Question Papers</p><p className="text-xs text-muted-foreground">{paper.grade || "All classes"}· </p><p className="mt-1 text-xs text-muted-foreground">Maximum currently available: {paper.available_sets}</p></div>
                <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:shrink-0 sm:gap-3">
                  <div className="flex items-center rounded-full border border-border" aria-label={`Quantity for ${paper.title}`}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => setQuantity(paper, paper.quantity - 1)} aria-label={`Decrease ${paper.title} quantity`}><Minus className="h-3.5 w-3.5" /></Button>
                    <span className="w-8 text-center text-sm font-bold" data-testid={`paper-quantity-${paper.id}`}>{paper.quantity}</span>
                    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" disabled={paper.quantity >= paper.available_sets || totalQuantity >= 50} onClick={() => setQuantity(paper, paper.quantity + 1)} aria-label={`Increase ${paper.title} quantity`}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                  <div className="ml-auto min-w-16 text-right sm:min-w-20">
                    {linePricing(paper).discount > 0 && <p className="text-xs text-muted-foreground line-through">₹{linePricing(paper).subtotal}</p>}
                    <span className="font-heading font-bold">₹{linePricing(paper).total}</span>
                    {linePricing(paper).discount > 0 && <p className="text-xs font-semibold text-primary">10% off</p>}
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => remove(paper.id)} aria-label={`Remove ${paper.title}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              </div>
            ))}
            {!items.length && <div className="py-12 text-center"><ShoppingCart className="mx-auto h-10 w-10 text-muted-foreground" /><p className="mt-3 font-medium">Your cart is empty</p><Link to="/student/plans" className="mt-2 inline-block text-sm font-medium text-primary">Browse question papers</Link></div>}
          </div>
        </section>
        <aside className="h-fit rounded-lg border border-border bg-background p-6 lg:sticky lg:top-24">
          <h2 className="font-heading text-xl font-bold">Order summary</h2>
          <div className="mt-5 flex justify-between text-sm"><span className="text-muted-foreground">Paper sets ({totalQuantity})</span><span>₹{subtotal}</span></div>
          {discount > 0 && <div className="mt-2 flex justify-between text-sm font-medium text-primary" data-testid="paper-discount"><span>3-set discount (10%)</span><span>−₹{discount}</span></div>}
          <div className="my-4 border-t border-border" />
          <div className="flex justify-between font-heading text-xl font-black"><span>Total</span><span>₹{total}</span></div>
          {mode === "test" && <p className="mt-3 rounded-md bg-secondary p-3 text-xs text-muted-foreground">Test mode: payment is simulated and the papers unlock immediately.</p>}
          <Button className="mt-6 w-full gap-2 rounded-full" disabled={!items.length || paying} onClick={pay} data-testid="pay-now">
            {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {paying ? "Processing…" : `Pay ₹${total}`}
          </Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">Secure payment powered by Razorpay</p>
        </aside>
      </div>
    </DashboardShell>
  );
}
